<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { VDataTableServer, VSelect } from 'vuetify/components';
import { useGetOrganizationGroups, useSearchUsers, CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import {
  checkMembershipCreatePermission,
  checkMembershipDeletePermission,
} from '@ilteducation/customer-mgmt-permissions';
import { ScrollingDataTableServer } from '@ilteducation/components';
import PermissionLock from '../el/PermissionLock.vue';
import type { SortOrder, FetchItems } from '@ilteducation/components';
import { useComboboxName } from '../composables/useComboboxName';
import { useLocale } from '../composables/useLocale';
import { usePermissions } from '../composables/usePermissions';
import GroupLabel from '../el/GroupLabel.vue';

const { t } = useLocale();
const { xs } = useDisplay();

const { loadGroups, groups, loading: groupsLoading } = useGetOrganizationGroups();
const availableGroups = computed(() => groups?.value?.data || []);
const { searchUsers, fetchAllGroupMemebers } = useSearchUsers();
const { permissionContext, getCallerAssociation, createTargetAssociation, getGroupMemberships, reporterWithReason } =
  usePermissions();

const props = withDefaults(
  defineProps<{
    organizationUid: string;
    sortBy?: string;
    sortOrder?: SortOrder;
    groupUid?: string;
    initialSelectedMembers?: string[];
  }>(),
  {
    sortBy: 'fullname',
    sortOrder: 'asc',
    groupUid: undefined,
    initialSelectedMembers: () => [],
  },
);
const selectedMembers = defineModel<string[]>({ default: [] });
const initialSelectedMembers = props.initialSelectedMembers;

type Item = CustomerMgmtTypes.SearchUsersResponse['data'][number] & { role: string };
type SearchUsersResponseWithRole = CustomerMgmtTypes.SearchUsersResponse & {
  data: Item[];
};

// Per-row permission to add/remove this user to/from the (fixed) group. Only applies when editing an
// existing group (`groupUid` set); when creating a new group there is no group to gate against.
const permissionsEnabled = computed(() => !!props.groupUid);
const permissionByUserUid = ref<Record<string, ReturnType<typeof reporterWithReason>>>({});
const isRowSelectable = (uid: string): boolean =>
  !permissionsEnabled.value || permissionByUserUid.value[uid]?.allowed === true;

const evaluateRowPermission = async (userItem: Item) => {
  const groupUid = props.groupUid;
  if (!groupUid || permissionByUserUid.value[userItem.uid]) {
    return;
  }
  const orgAssoc = userItem.associations?.find((a) => a.organization.uid === props.organizationUid);
  if (!orgAssoc) {
    // should never happen just to make ts happy
    permissionByUserUid.value = {
      ...permissionByUserUid.value,
      [userItem.uid]: { allowed: false, reason: 'not_permitted' },
    };
    return;
  }

  const organization = orgAssoc.organization;
  const getTargetAssociation = async () => createTargetAssociation(userItem, orgAssoc);
  const isMember = initialSelectedMembers.includes(userItem.uid);

  const result = isMember
    ? await checkMembershipDeletePermission(
        permissionContext.value,
        {
          user: userItem,
          group: { uid: groupUid, organization },
          source: organization.memberships.find((m) => m.groupUid === groupUid)?.source ?? userItem.source,
        },
        getCallerAssociation,
        getTargetAssociation,
        getGroupMemberships,
        reporterWithReason,
      )
    : await checkMembershipCreatePermission(
        permissionContext.value,
        { uid: groupUid, organization },
        { uid: userItem.uid },
        getCallerAssociation,
        getTargetAssociation,
        getGroupMemberships,
        reporterWithReason,
      );

  permissionByUserUid.value = { ...permissionByUserUid.value, [userItem.uid]: result };
};

const theTable = ref<typeof ScrollingDataTableServer | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const searchString = ref<string | undefined>(undefined);
const selectedGroupUids = ref<string[] | undefined>(undefined);
const initialMembersFetched = ref(false);
const initialGroupMembers = ref<Item[]>([]);

const membersToShowAtTop = computed(() => {
  if (!initialMembersFetched.value || searchString.value) {
    return [];
  }
  if (selectedGroupUids.value && selectedGroupUids.value.length > 0) {
    return initialGroupMembers.value.filter((member) =>
      selectedGroupUids.value?.some((groupUid) =>
        member.associations?.some((association) =>
          association.organization.memberships.some((m) => m.groupUid === groupUid),
        ),
      ),
    );
  }
  return initialGroupMembers.value;
});

const fetchInitialGroupMembers = async () => {
  if (!props.groupUid) {
    initialGroupMembers.value = [];
    initialMembersFetched.value = true;
    return;
  }

  try {
    const members = await fetchAllGroupMemebers(props.groupUid);
    initialGroupMembers.value = members.map((user) => {
      const userRole = user.associations?.find((a) => a.organizationUid === props.organizationUid)?.role || 'student';
      return {
        ...user,
        role: userRole,
      } satisfies Item;
    });
    initialMembersFetched.value = true;
    initialGroupMembers.value.forEach((user) => evaluateRowPermission(user));
  } catch (error) {
    console.error('Error fetching initial group members:', error);
    initialGroupMembers.value = [];
    initialMembersFetched.value = true;
  }
};

onMounted(async () => {
  await Promise.all([loadGroups(props.organizationUid, 1, 100, 'name', 'asc'), fetchInitialGroupMembers()]);
});

const headers = computed((): VDataTableServer['$props']['headers'] => [
  { title: t('lcp.name'), key: 'fullname', align: 'start', sortable: true, maxWidth: '20vw', nowrap: true },
  { title: t('lcp.username'), key: 'username', align: 'start', sortable: true, maxWidth: '20vw', nowrap: true },
  { title: t('lcp.role'), key: 'role', align: 'start', sortable: false, maxWidth: '10vw', nowrap: true },
]);

const groupFilterMenuProps = useComboboxName(() => t('lcp.filter_by_group'));

// Named after the row it selects: on its own the checkbox is one of a column of identical boxes.
const selectMemberLabel = (item: Item) => `${t('lcp.select')}: ${item.fullname}`;

const filters = computed(() => ({
  organizations: {
    uids: [props.organizationUid],
    ...(selectedGroupUids.value ? { groups: { uids: selectedGroupUids.value } } : {}),
  },
}));

const onSearchUsers: FetchItems<typeof searchUsers> = async (args): Promise<SearchUsersResponseWithRole> => {
  const { pageNumber, pageSize, sortBy, sortOrder, q, filters } = args;
  const response = await searchUsers({
    q,
    pageNumber,
    pageSize,
    sortBy: sortBy as CustomerMgmtTypes.SearchUsersRequest['sortBy'],
    sortOrder,
    filters,
  });

  const modifiedResponseData = response.data.map((user) => {
    const userRole = user.associations?.find((a) => a.organizationUid === props.organizationUid)?.role || 'student';
    return {
      ...user,
      role: userRole,
    } satisfies Item;
  });

  modifiedResponseData.forEach((user) => evaluateRowPermission(user));

  return {
    ...response,
    data: modifiedResponseData,
    meta: response.meta,
  } satisfies SearchUsersResponseWithRole;
};

const onGroupFilterChange = (value: Readonly<string[]> | null) => {
  selectedGroupUids.value = value && value.length > 0 ? [...value] : undefined;
  theTable.value?.reset();
  theTable.value?.reloadItems();
};
const onSearchStringChange = (value: string) => {
  if (value === searchString.value) {
    return;
  }

  searchString.value = value;
  theTable.value?.reset();
};
const onClearSearchString = () => {
  searchString.value = undefined;
  theTable.value?.reset();
};

const isItemSelected = (uid: string) => {
  return selectedGroupUids.value?.includes(uid) || false;
};

// Select all checkbox logic — only operates on rows the caller is permitted to toggle.
const currentlyLoadedItems = computed(() => {
  return (theTable.value?.listItems ?? []) as Item[];
});

const selectableLoadedItems = computed(() => currentlyLoadedItems.value.filter((item) => isRowSelectable(item.uid)));

const allSelectedItems = computed(() => {
  if (selectableLoadedItems.value.length === 0) return false;
  return selectableLoadedItems.value.every((item) => selectedMembers.value.includes(item.uid));
});

const someSelectedItems = computed(() => {
  if (selectableLoadedItems.value.length === 0) return false;
  const someSelected = selectableLoadedItems.value.some((item) => selectedMembers.value.includes(item.uid));
  return someSelected && !allSelectedItems.value;
});

const toggleSelectAll = (value: boolean) => {
  if (value) {
    // Select all currently loaded selectable items
    const currentUids = selectableLoadedItems.value.map((item) => item.uid);
    const newSelection = new Set([...selectedMembers.value, ...currentUids]);
    selectedMembers.value = [...newSelection];
  } else {
    // Deselect all currently loaded selectable items
    const currentUids = new Set(selectableLoadedItems.value.map((item) => item.uid));
    selectedMembers.value = selectedMembers.value.filter((uid) => !currentUids.has(uid));
  }
};

defineExpose({
  selectedMembers,
  reset: () => {
    selectedMembers.value = initialSelectedMembers || [];
  },
});
</script>

<template>
  <v-row class="ga-4" no-gutters>
    <v-col :cols="xs ? 12 : 'auto'" class="d-flex flex-1-1" :style="{ maxWidth: xs ? '100%' : '50%' }">
      <v-select
        :item-title="(item) => `${item.name} (${item.type})`"
        :items="availableGroups"
        item-value="uid"
        :loading="groupsLoading"
        :label="t('lcp.filter_by_group')"
        :no-data-text="t('lcp.no_groups_available')"
        :menu-props="groupFilterMenuProps"
        multiple
        block
        hide-details
        chipss
        clearable
        clear-icon="mdi-close"
        @update:model-value="onGroupFilterChange"
      >
        <template #selection="selectionProps">
          <v-chip v-if="selectionProps.index < 2">
            <GroupLabel
              :name="selectionProps.item.raw.name"
              :years-in-school="selectionProps.item.raw.yearsInSchool"
              size="small"
            />
          </v-chip>
          <span v-if="selectionProps.index === 2" class="text-grey text-caption align-self-center">
            (+{{ selectedGroupUids?.length ? selectedGroupUids.length - 2 : 0 }} ...)
          </span>
        </template>
        <template #item="{ item, props }">
          <v-list-item v-bind="props">
            <template #title>
              <v-row no-gutters class="d-flex flex-nowrap align-center justify-space-between">
                <!-- The option itself carries the selected state, so this one is decoration and
                is kept out of the way of both the reading order and the tab order. -->
                <v-checkbox
                  :model-value="isItemSelected(item.raw.uid)"
                  hide-details
                  tabindex="-1"
                  aria-hidden="true"
                ></v-checkbox>
                <GroupLabel :name="item.raw.name" :years-in-school="item.raw.yearsInSchool" />
                <v-spacer></v-spacer>
                <span class="on-surface-medium">{{ t(`lcp.group_type_${item.raw.type}`) }}</span>
              </v-row>
            </template>
          </v-list-item>
        </template>
      </v-select>
    </v-col>
    <v-col :cols="xs ? 12 : 'auto'" class="d-flex flex-1-1" :style="{ maxWidth: xs ? '100%' : '50%' }">
      <v-text-field
        ref="searchInput"
        name="searchString"
        :label="t('lcp.search_users')"
        block
        clearable
        clear-icon="mdi-close"
        :singleLine="false"
        append-inner-icon="mdi-magnify"
        hide-details
        @change="() => onSearchStringChange(searchInput?.value?.trim() || '')"
        @click:clear="onClearSearchString"
        @click:append-inner="() => onSearchStringChange(searchInput?.value?.trim() || '')"
      />
    </v-col>
    <v-col cols="12">
      <ScrollingDataTableServer
        ref="theTable"
        list="usersForGroups"
        :loading="!initialMembersFetched"
        :headers="headers"
        :fetchItems="onSearchUsers"
        :itemsToPrepend="membersToShowAtTop"
        :itemsPerPage="50"
        :search-string="searchString"
        :filters="filters"
        show-select
        item-value="uid"
        v-model="selectedMembers"
        :sort-by="sortBy"
        :sort-order="sortOrder"
      >
        <!-- Select all checkbox for currently loaded items -->
        <template #[`header.data-table-select`]>
          <v-checkbox-btn
            :model-value="allSelectedItems"
            :indeterminate="someSelectedItems"
            :disabled="selectableLoadedItems.length === 0"
            :aria-label="t('lcp.select_all')"
            @update:model-value="toggleSelectAll"
          />
        </template>

        <!-- per-row select: lock users the caller can't add/remove for this group -->
        <template #[`item.data-table-select`]="{ item, internalItem, isSelected, toggleSelect }">
          <v-checkbox-btn
            v-if="isRowSelectable((item as Item).uid)"
            :model-value="isSelected(internalItem)"
            :aria-label="selectMemberLabel(item as Item)"
            @update:model-value="toggleSelect(internalItem)"
          />
          <div v-else class="d-flex align-center justify-center" style="width: 40px; height: 40px">
            <PermissionLock
              :reason="permissionByUserUid[(item as Item).uid]?.reason ?? 'not_permitted'"
              color="primary"
            />
          </div>
        </template>

        <!-- translate the roles -->
        <template #[`item.role`]="{ item }">
          <span>{{ t(`lcp.role_${item.role}`) }}</span>
        </template>
      </ScrollingDataTableServer>
    </v-col>
  </v-row>
</template>
